import { createSelector } from 'reselect';
import type { RootState } from '../index';
import type { PanelMapInterface, UnitInterface } from './types';
import { getConnections } from 'store/connections/selectors';
import { sortByFlowOrder } from 'containers/ReportPanel/sortByFlowOrder';

export const getUnitState = (state: RootState) =>
  state.unitConnGroup.present.unitState;

export const getPorts = (state: RootState) =>
  state.unitConnGroup.present.unitState.ports;

export const getActiveUnitId = (state: RootState) =>
  state.unitConnGroup.present.unitState.activeUnit;

export const getActiveUnitTitle = (state: RootState) =>
  state.unitConnGroup.present.unitState.activeUnitTitle ?? null;

export const getUnits = (state: RootState): UnitInterface[] =>
  state.unitConnGroup.present.unitState.units;

export const getPanels = (state: RootState): PanelMapInterface =>
  state.unitConnGroup.present.unitState.panels;

export const getActiveUnit = createSelector(
  [getUnits, getActiveUnitId],
  (units, id) => units.find((u) => u.key === id) ?? null,
);

export const getSimulationResults = (state: RootState) =>
  state.unitConnGroup.present.unitState.simulationResults;

export const getChamberAnalysisResults = (state: RootState) =>
  state.unitConnGroup.present.unitState.chamberAnalysisResults;

export const getSimulationResultByKey = (unitKey: string) =>
  createSelector([getSimulationResults], (results) => results[unitKey]);

export const getChamberAnalysisResultByKey = (unitKey: string) =>
  createSelector([getChamberAnalysisResults], (results) => results[unitKey]);

export const getActiveUnitSimulationResult = createSelector(
  [getActiveUnitId, getSimulationResults],
  (activeUnitId, simulationResults) => {
    if (!activeUnitId) return undefined;
    return simulationResults[activeUnitId];
  },
);

export const getChamberAnalysisResult = createSelector(
  [getActiveUnitId, getChamberAnalysisResults],
  (activeUnitId, chamberAnalysisResults) => {
    if (!activeUnitId) return undefined;
    return chamberAnalysisResults[activeUnitId];
  },
);

export const getActiveUnitWarnings = createSelector(
  [getActiveUnitSimulationResult],
  (simulationResult) => {
    if (
      !simulationResult?.warnings ||
      Object.keys(simulationResult.warnings).length === 0
    ) {
      return {
        MINOR: [],
        MODERATE: [],
        MAJOR: [],
      };
    }

    return simulationResult.warnings;
  },
);

export const getUnitsSortedByFlowOrder = createSelector(
  [getUnits, getConnections],
  (units, connections) => {
    return sortByFlowOrder(units, connections);
  },
);

export const getCanUndo = (state: RootState) =>
  state.unitConnGroup.past.length > 0;

export const getCanRedo = (state: RootState) =>
  state.unitConnGroup.future.length > 0;

export const getSelectedNodes = (state: RootState) =>
  state.unitConnGroup.present.unitState.selectedNodes;

export const getSelectedUnits = createSelector(
  [getUnits, getSelectedNodes],
  (units, nodes) => {
    const keys = units.map((u) => u.key);
    return nodes.filter((node) => keys.includes(node));
  },
);

export const getGroups = (state: RootState) =>
  state.unitConnGroup.present.unitState.groups;

export const getGroupMap = createSelector(
  [getGroups, getUnits],
  (groups, units) => {
    return units.reduce((acc, curr) => {
      const unitKey = curr.key;
      const group = Object.values(groups).find((g) =>
        g.unitKeys.includes(unitKey),
      );
      return {
        ...acc,
        [unitKey]: group?.key,
      };
    }, {});
  },
);

export const getActiveGroup = createSelector(
  [getGroups, getActiveUnitId],
  (groups, id) => {
    if (!id) {
      return null;
    }
    return groups[id] || null;
  },
);
